// PopupProyecto.js — Ventana emergente (pop up) por proyecto y por país.
//
// Se configura en Admin → Proyecto → Pop ups (config_json.popups) y está apagado
// salvo que el proyecto lo active. Cada pop up es una imagen a la que se le
// asigna en qué países debe verse; gana el PRIMERO de la lista que le calce al
// visitante, así que un pop up sin países al final de la lista funciona como
// "para los demás países".
//
// Al cerrarlo (la ✕ o el fondo) queda marcado en localStorage y no vuelve a
// abrirse hasta que el visitante borre las cookies/datos del sitio. Subir la
// "versión" del pop up en el admin lo vuelve a mostrar a todo el mundo.
//
// Un proyecto sin `popups` no pinta nada: el resto de proyectos no cambia.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { usePais } from '../contexts/PaisContext';
import { normalizarPais } from '../utils/pais';
import { BACKEND_BASE } from '../services/api';

// Ancho máximo del pop up cuando el admin no fija uno.
const ANCHO_DEFECTO = 650;
// Cuánto se tolera estirar una imagen por encima de su tamaño real antes de que
// se note borrosa. Sin esto, un póster de 600 px de ancho no llegaría nunca al
// ancho configurado.
const ESTIRADO_MAXIMO = 1.25;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3800;
  background: ${p => p.$fondo || 'rgba(15, 23, 42, 0.62)'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  animation: popupProyectoIn 0.25s ease;
  @keyframes popupProyectoIn { from { opacity: 0; } to { opacity: 1; } }
  /* En móvil el margen se reduce para que la imagen aproveche el ancho. */
  @media (max-width: 600px) { padding: 0.6rem; }
`;

// La tarjeta NO hace scroll: lo hace su interior, para que la ✕ quede siempre
// visible aunque la imagen sea muy alta. El ancho es fluido: nunca pasa del
// configurado ni del espacio disponible en pantalla.
const Card = styled.div`
  position: relative;
  width: min(${p => p.$ancho || ANCHO_DEFECTO}px, 100%);
  max-height: calc(100vh - 2.4rem);
  background: ${p => p.$bg || '#ffffff'};
  border-radius: ${p => p.$radio || '10px'};
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  @media (max-width: 600px) { max-height: calc(100vh - 1.2rem); }
`;

const Scroll = styled.div`
  max-height: calc(100vh - 2.4rem);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  line-height: 0;
  @media (max-width: 600px) { max-height: calc(100vh - 1.2rem); }
`;

const Img = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const Cerrar = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: ${p => p.$bg || '#2f2f3d'};
  color: ${p => p.$color || '#ffffff'};
  font-size: 1.25rem;
  line-height: 1;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
  transition: filter 0.15s, transform 0.15s;
  &:hover { filter: brightness(1.15); transform: scale(1.06); }
`;

// Rutas donde un pop up estorbaría (clase interna: video, chat, evaluación…).
const RUTAS_EXCLUIDAS = ['/admin', '/mentor/', '/consulta/', '/consultaiphone/', '/evaluacion', '/practica', '/reto'];
const RUTAS_HOME = ['/', '/login', '/register'];

// ¿La lista incluye alguno de los identificadores del visitante?
// Lista vacía = ese criterio no filtra (aplica a todos).
const calza = (lista, valores) => {
  if (!Array.isArray(lista) || lista.length === 0) return true;
  const ids = (Array.isArray(valores) ? valores : [valores]).filter(Boolean).map(normalizarPais);
  if (ids.length === 0) return false;
  return lista.some(x => ids.includes(normalizarPais(x)));
};

export const claveCierre = (proyectoId, pop) => `popup_cerrado_${proyectoId || 0}_${pop.id}_v${pop.version || 1}`;

const yaCerrado = (proyectoId, pop) => {
  try { return localStorage.getItem(claveCierre(proyectoId, pop)) === '1'; } catch (_) { return false; }
};

const resolveUrl = (u) => (!u ? '' : (u.startsWith('http') || u.startsWith('data:') ? u : `${BACKEND_BASE}/${u}`));

// Vista pura del pop up: la usa también la vista previa del admin.
//
// El ancho configurado (650 px por defecto) es un MÁXIMO: la imagen se precarga
// para no estirarla mucho más allá de su tamaño real, que se vería borrosa. En
// pantallas estrechas manda el ancho disponible. Si la imagen es más alta que la
// pantalla, la ventana crece hasta el alto disponible y el resto se recorre con
// scroll, con la ✕ siempre fija.
export const PopupVista = ({ pop, onCerrar }) => {
  const src = resolveUrl(pop.imagen);
  const tope = Number(pop.ancho) || ANCHO_DEFECTO;
  const [anchoReal, setAnchoReal] = useState(0);

  useEffect(() => {
    setAnchoReal(0);
    if (!src) return undefined;
    let vivo = true;
    const img = new Image();
    img.onload = () => { if (vivo) setAnchoReal(img.naturalWidth || 0); };
    img.src = src;
    return () => { vivo = false; };
  }, [src]);

  const ancho = anchoReal ? Math.min(tope, Math.round(anchoReal * ESTIRADO_MAXIMO)) : tope;

  return (
  <Overlay
    $fondo={pop.color_fondo}
    onMouseDown={e => { if (e.target === e.currentTarget && pop.cerrar_fuera !== false) onCerrar(); }}
  >
    <Card $ancho={ancho} $radio={pop.radio} $bg={pop.color_tarjeta}>
      <Cerrar
        type="button"
        aria-label="Cerrar"
        $bg={pop.color_cerrar}
        $color={pop.color_cerrar_texto}
        onClick={onCerrar}
      >
        ✕
      </Cerrar>
      <Scroll>
        {pop.enlace ? (
          <a
            href={pop.enlace}
            target={pop.enlace_nueva_pestana === false ? '_self' : '_blank'}
            rel="noopener noreferrer"
            onClick={() => { if (pop.cerrar_al_click !== false) onCerrar(); }}
          >
            <Img src={src} alt={pop.alt || pop.nombre || ''} />
          </a>
        ) : (
          <Img src={src} alt={pop.alt || pop.nombre || ''} />
        )}
      </Scroll>
    </Card>
  </Overlay>
  );
};

const PopupProyecto = () => {
  const { proyecto } = useProject();
  const { user } = useAuth();
  const { audiencia } = usePais();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const cfg = proyecto?.config_json?.popups || null;
  const items = useMemo(
    () => (Array.isArray(cfg?.items) ? cfg.items.filter(p => p && p.activo !== false && p.imagen) : []),
    [cfg]
  );

  const paisIds = useMemo(() => (
    Array.isArray(audiencia?.paisIds) && audiencia.paisIds.length > 0
      ? audiencia.paisIds
      : [audiencia?.pais, audiencia?.paisNombre].filter(Boolean)
  ), [audiencia]);

  const ruta = location.pathname;
  const rutaBloqueada = RUTAS_EXCLUIDAS.some(r => ruta === r || ruta.startsWith(r));
  const enHome = RUTAS_HOME.includes(ruta);

  // El pop up de este visitante es el PRIMERO de la lista que le calce; si ya lo
  // cerró no se muestra nada más. Buscar el siguiente sería peor: quien cerró el
  // de su país acabaría viendo el comodín de "los demás países".
  const pop = useMemo(() => {
    if (cfg?.activo !== true || rutaBloqueada) return null;
    const candidato = items.find(p => {
      if (p.donde === 'home' && !enHome) return false;
      if (p.audiencia === 'sesion' && !user) return false;
      if (p.audiencia === 'invitados' && user) return false;
      if (!calza(p.paises, paisIds)) return false;
      return calza(p.subroles, audiencia?.subrol);
    });
    if (!candidato || yaCerrado(proyecto?.id, candidato)) return null;
    return candidato;
  }, [cfg, items, rutaBloqueada, enHome, user, paisIds, audiencia, proyecto]);

  // Pequeño retraso para que el pop up no aparezca antes que la página.
  useEffect(() => {
    setVisible(false);
    if (!pop) return undefined;
    const ms = Math.max(0, Number(pop.retraso ?? 0.6) * 1000);
    const t = setTimeout(() => setVisible(true), ms);
    return () => clearTimeout(t);
  }, [pop]);

  // Cerrar con Escape, igual que la ✕.
  useEffect(() => {
    if (!visible || !pop) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') cerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pop]);

  if (!pop || !visible) return null;

  function cerrar() {
    try { localStorage.setItem(claveCierre(proyecto?.id, pop), '1'); } catch (_) { /* noop */ }
    setVisible(false);
  }

  return <PopupVista pop={pop} onCerrar={cerrar} />;
};

export default PopupProyecto;
